import { useEffect, useState } from 'react';

/**
 * Phase 4.2 — POS catalogue hook.
 *
 * Replaces the hardcoded CATALOGUE / CATEGORY_COLOURS arrays in
 * PosPage. Fetches /api/products + /api/categories (active only) on
 * mount and on tab focus. Both endpoints are cashier-readable.
 */

export interface ApiCategory {
  id: string;
  name: string;
  slug: string;
  tone: string;
  iconKey: string;
  sortOrder: number;
}

export interface ApiSize { name: string; priceDelta: number }
export interface ApiAddon { name: string; priceCents: number }

export interface ApiProduct {
  id: string;
  categoryId: string;
  name: string;
  description: string | null;
  priceCents: number;
  originalPriceCents: number | null;
  diet: 'VEG' | 'NON_VEG' | 'EGG';
  badge: 'NONE' | 'TRENDING' | 'MUST_TRY' | 'HOT';
  available: number;
  imageUrl: string | null;
  barcode: string | null;
  requiresConfiguration: boolean;
  sizes: ApiSize[];
  defaultIngredients: string[];
  availableAddons: ApiAddon[];
  sortOrder: number;
}

/**
 * Stale-while-revalidate: on mount, hydrate from localStorage (instant
 * render of yesterday's data) then re-fetch in the background. Eliminates
 * the "flash of seed data" you see when the JS bundle loads faster than
 * the API can reply.
 */
const CACHE_KEY = 'codedpos.menu.v1';

interface Cached { categories: ApiCategory[]; products: ApiProduct[] }

function readCache(): Cached | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Cached;
    if (!Array.isArray(parsed.categories) || !Array.isArray(parsed.products)) return null;
    return parsed;
  } catch { return null; }
}
function writeCache(value: Cached) {
  try { localStorage.setItem(CACHE_KEY, JSON.stringify(value)); } catch { /* quota / disabled */ }
}

export function useMenu() {
  const initial = readCache();
  const [categories, setCategories] = useState<ApiCategory[]>(initial?.categories ?? []);
  const [products, setProducts]     = useState<ApiProduct[]>(initial?.products ?? []);
  // `loading` reflects "have we ever heard from the server" — used by the
  // UI to decide whether to show a skeleton (initial only) or just render.
  // If we have a cache, we're "ready" immediately; the fetch refreshes
  // silently in the background.
  const [loading, setLoading] = useState(initial == null);
  const [error, setError] = useState<string | null>(null);

  async function load() {
    try {
      const [cr, pr] = await Promise.all([
        fetch('/api/categories', { credentials: 'include' }),
        fetch('/api/products', { credentials: 'include' }),
      ]);
      if (!cr.ok || !pr.ok) throw new Error(`HTTP ${cr.status}/${pr.status}`);
      const cd = await cr.json();
      const pd = await pr.json();
      const nextCategories = cd.categories ?? [];
      const nextProducts   = pd.products   ?? [];
      setCategories(nextCategories);
      setProducts(nextProducts);
      writeCache({ categories: nextCategories, products: nextProducts });
      setError(null);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
    const onFocus = () => { void load(); };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, []);

  return { categories, products, loading, error, refresh: load };
}
